/**
 * SHELL — display formatters. Pure, dependency-free.
 */

/** "1:23.45" from milliseconds. */
export function formatTime(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '--:--';
  const totalCentis = Math.round(ms / 10);
  const centis = totalCentis % 100;
  const totalSecs = Math.floor(totalCentis / 100);
  const secs = totalSecs % 60;
  const mins = Math.floor(totalSecs / 60);
  return `${mins}:${secs.toString().padStart(2, '0')}.${centis.toString().padStart(2, '0')}`;
}

/** Thousands-separated integer score. */
export function formatScore(score: number): string {
  return Math.round(score).toLocaleString('en-US');
}

/** "1st", "2nd", "3rd", "11th"... */
export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? 'th' : (s[v % 10] ?? 'th');
  return `${n}${suffix}`;
}

/** Compact rank like "#1,234" or "—". */
export function formatRank(rank: number | undefined): string {
  return rank === undefined ? '—' : `#${rank.toLocaleString('en-US')}`;
}

/** "Top 3%" given a 0..100 percentile (higher = better). */
export function formatPercentile(percentile: number | undefined): string | undefined {
  if (percentile === undefined) return undefined;
  const top = Math.max(0, Math.min(100, Math.round(100 - percentile)));
  return top <= 0 ? 'Top spot' : `Top ${top}%`;
}

/** A friendly date label, e.g. "Saturday, June 14". */
export function formatDateLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map((p) => Number.parseInt(p, 10));
  if (!y || !m || !d) return dateKey;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Milliseconds until the next UTC midnight (when today's run resets). */
export function msUntilUtcMidnight(now: Date = new Date()): number {
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0);
  return next - now.getTime();
}

/** "in 5h 12m" style countdown from a millisecond duration. */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return 'now';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.ceil(ms / 1000)}s`;
}
